/**
 * ============================================
 * 🔧 APP CONFIGURATION - แก้ไขที่นี่ที่เดียว!
 * ============================================
 *
 * ไฟล์นี้รวม config ทั้งหมดไว้ที่เดียว
 * - ค่า Default สำหรับ Development
 * - สามารถ Override ด้วย .env.local ได้
 *
 * Priority: window.APP_CONFIG > .env.local > .env > Default values
 */

// ============================================
// 🌐 RUNTIME CONFIG INTERFACE
// ============================================

declare global {
  interface Window {
    APP_CONFIG?: {
      PARSE_V2_SERVER_URL?: string;
      PARSE_V2_APP_ID?: string;
      PARSE_V2_MASTER_KEY?: string;
      APP_NAME?: string;
      APP_VERSION?: string;
      DEV_MODE?: boolean;
      DEBUG_MODE?: boolean;
      DEFAULT_LANGUAGE?: string;
      AVAILABLE_LANGUAGES?: string;
      ENABLE_2FA?: boolean;
      ENABLE_SOUND_NOTIFICATIONS?: boolean;
      HIDDEN_FEATURES?: string;
    };
  }
}

// ============================================
// 🌐 SERVER CONFIGURATION
// ============================================

/**
 * Backend Server URL (สำหรับ Vite Dev Proxy)
 * - Development: Vite จะ proxy /api/v1 ไปที่ URL นี้
 * - Production: ไม่ใช้ (ใช้ PARSE_SERVER_URL โดยตรง)
 */
export const API_BACKEND_URL = 
  import.meta.env.VITE_API_BACKEND_URL || 
  'http://178.128.85.99:5000';

/**
 * Parse Server API Path
 * - Development: /api/v1 (ผ่าน proxy)
 * - Production: https://olive888.net/api/v1
 */
export const PARSE_SERVER_URL = 
  import.meta.env.VITE_PARSE_V1_SERVER_URL || 
  import.meta.env.VITE_PARSE_SERVER_URL || 
  '/api/v1';

// ============================================
// 🔑 PARSE SERVER CREDENTIALS
// ============================================

/**
 * Parse Application ID
 */
export const PARSE_APP_ID = 
  import.meta.env.VITE_PARSE_V1_APP_ID || 
  import.meta.env.VITE_PARSE_APP_ID || 
  '27b84e36f571bd16760a11f5963b6f5b22d87e6086ad7cf444eab17de59a24ef9aee0c950db6d6385555555555';

/**
 * Parse Master Key (สำหรับ Admin operations)
 */
export const PARSE_MASTER_KEY = 
  import.meta.env.VITE_PARSE_V1_MASTER_KEY || 
  import.meta.env.VITE_PARSE_MASTER_KEY || 
  '59089e920f5ed919e2657fb15eabab82420ee0da6d9f10c1f06a89dfecef06698e2f584efaaba89d5555555555';

/**
 * Parse JavaScript Key (optional)
 */
export const PARSE_JS_KEY = 
  import.meta.env.VITE_PARSE_V1_JS_KEY || 
  import.meta.env.VITE_PARSE_JS_KEY || 
  '';

// ============================================
// 🎛️ APP SETTINGS
// ============================================

/**
 * App Name
 */
export const APP_NAME =
  window.APP_CONFIG?.APP_NAME ||
  import.meta.env.VITE_APP_NAME ||
  'Admin Panel Pro';

/**
 * App Version
 */
export const APP_VERSION =
  window.APP_CONFIG?.APP_VERSION ||
  import.meta.env.VITE_APP_VERSION ||
  '2.0.0';

/**
 * Dev Mode - แสดง features ที่ยังไม่พร้อม
 */
export const DEV_MODE =
  window.APP_CONFIG?.DEV_MODE ??
  (import.meta.env.VITE_DEV_MODE === 'true');

/**
 * Debug Mode - แสดง logs เพิ่มเติม
 */
export const DEBUG_MODE =
  window.APP_CONFIG?.DEBUG_MODE ??
  (import.meta.env.VITE_DEBUG_MODE === 'true');

// ============================================
// 🌍 i18n SETTINGS
// ============================================

/**
 * Default Language
 */
export const DEFAULT_LANGUAGE =
  window.APP_CONFIG?.DEFAULT_LANGUAGE ||
  import.meta.env.VITE_DEFAULT_LANGUAGE ||
  'th';

/**
 * Available Languages
 */
export const AVAILABLE_LANGUAGES = (
  window.APP_CONFIG?.AVAILABLE_LANGUAGES ||
  import.meta.env.VITE_AVAILABLE_LANGUAGES ||
  'th,en,lo,km'
).split(',');

// ============================================
// 🚀 FEATURE FLAGS
// ============================================

/**
 * Hidden Features (comma-separated)
 */
export const HIDDEN_FEATURES = (
  window.APP_CONFIG?.HIDDEN_FEATURES ||
  import.meta.env.VITE_HIDE_FEATURES ||
  ''
).split(',').filter(Boolean);

/**
 * Enable 2FA
 */
export const ENABLE_2FA =
  window.APP_CONFIG?.ENABLE_2FA ??
  (import.meta.env.VITE_ENABLE_2FA !== 'false');

/**
 * Enable Sound Notifications
 */
export const ENABLE_SOUND_NOTIFICATIONS =
  window.APP_CONFIG?.ENABLE_SOUND_NOTIFICATIONS ??
  (import.meta.env.VITE_ENABLE_SOUND_NOTIFICATIONS !== 'false');

// ============================================
// 📊 PARSE V2 CONFIG (Future)
// ============================================

/**
 * Parse Server V2 API Path
 * - Runtime: window.APP_CONFIG (แก้ใน public/config.js หลัง build)
 * - Build time: VITE_PARSE_V2_SERVER_URL (แก้ใน .env.production)
 * - Development: /api/v2 (ผ่าน Vite proxy → http://178.128.85.99:5002/api/v1)
 * - Production: https://api.olive888.net/api/v1 (เรียกโดยตรง - Backend V2 มี CORS ตั้งไว้แล้ว)
 *
 * ⚠️ หมายเหตุ: http://178.128.85.99:5002 = https://api.olive888.net
 * ⚠️ Priority: window.APP_CONFIG > .env > Default
 */
export const PARSE_V2_SERVER_URL =
  window.APP_CONFIG?.PARSE_V2_SERVER_URL ||
  import.meta.env.VITE_PARSE_V2_SERVER_URL ||
  (import.meta.env.MODE === 'production'
    ? 'https://api.olive888.net/api/v1'  // Production: เรียก api.olive888.net โดยตรง
    : '/api/v2');                         // Development: ใช้ relative path (Vite proxy → 178.128.85.99:5002)

export const PARSE_V2_APP_ID =
  window.APP_CONFIG?.PARSE_V2_APP_ID ||
  import.meta.env.VITE_PARSE_V2_APP_ID ||
  '';

export const PARSE_V2_MASTER_KEY =
  window.APP_CONFIG?.PARSE_V2_MASTER_KEY ||
  import.meta.env.VITE_PARSE_V2_MASTER_KEY ||
  '';

// ============================================
// 🔧 GO BACKEND CONFIG (Future)
// ============================================

export const GO_SERVER_URL = 
  import.meta.env.VITE_GO_SERVER_URL || 
  '/api/go';

// ============================================
// 📦 EXPORTED CONFIG OBJECTS
// ============================================

/**
 * Parse Server Configuration (รวมทุกค่า)
 */
export const PARSE_CONFIG = {
  serverURL: PARSE_SERVER_URL,
  appId: PARSE_APP_ID,
  masterKey: PARSE_MASTER_KEY,
  jsKey: PARSE_JS_KEY,
} as const;

/**
 * App Configuration
 */
export const APP_CONFIG = {
  name: APP_NAME,
  version: APP_VERSION,
  devMode: DEV_MODE,
  debugMode: DEBUG_MODE,
  defaultLanguage: DEFAULT_LANGUAGE,
  availableLanguages: AVAILABLE_LANGUAGES,
  hiddenFeatures: HIDDEN_FEATURES,
} as const;

/**
 * Server Configuration
 */
export const SERVER_CONFIG = {
  apiBackendUrl: API_BACKEND_URL,
  parseV1: PARSE_CONFIG,
  parseV2: {
    serverURL: PARSE_V2_SERVER_URL,
    appId: PARSE_V2_APP_ID,
    masterKey: PARSE_V2_MASTER_KEY,
  },
  go: {
    serverURL: GO_SERVER_URL,
  },
} as const;

// ============================================
// 🔍 DEBUG: Log Configuration
// ============================================

if (DEBUG_MODE) {
  console.log('🔧 App Config:', {
    name: APP_NAME,
    version: APP_VERSION,
    devMode: DEV_MODE,
    debugMode: DEBUG_MODE,
  });
  
  console.log('🌐 Server Config:', {
    apiBackendUrl: API_BACKEND_URL,
    parseServerUrl: PARSE_SERVER_URL,
    parseAppId: PARSE_APP_ID ? '***' + PARSE_APP_ID.slice(-8) : 'NOT SET',
    hasMasterKey: !!PARSE_MASTER_KEY,
  });
}

// ============================================
// 🛡️ TYPE EXPORTS
// ============================================

export type ParseConfig = typeof PARSE_CONFIG;
export type AppConfig = typeof APP_CONFIG;
export type ServerConfig = typeof SERVER_CONFIG;


